import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@/components/ui/modal/Dialog.js";
import { useTranslation } from 'react-i18next';
import { Check, Eye, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { IconButton } from '../common/icon-button';
import { PreviewVideoImageFull } from '../common/preview-video-image-full';
import { FallbackImage } from '../common/fallback-image';
import { buildImageOssResize } from '@/utils/build-image-oss-resize ';
import { agentCompanyAssetHooks, agentTemplateHooks, agentUserTemplateListHooks, agentUserVoiceListHooks, iamAccountHooks } from '@/manager';
import { LoadingOverlay } from '../common/loading-overlay';
import { AddTemplateIcon } from "../agent-dashboard-company-asset/assets";
import { DeleteConfirmModal, Tooltip } from '../ui/modal';
import { useStyleEditorMicroApp } from '@/hooks/use-style-editor-microapp';
import StyleEditorMicroApp, { VIDEO_STYLE_TYPE } from "@bhb-frontend/style-editor-microapp";
import { createPortal } from 'react-dom';
import { useToast } from '@/hooks/use-toast';
import { useParams } from 'react-router-dom';
import { MoreActionItem, MoreActionPopover } from '../common/more-action-popover';
import { MoreIcon, RenameIcon, TrashIcon } from '../ui/icon';
import { EditIcon } from '../agent-dashboard/assets';
import { RenameModal } from '../common/rename-modal';

type TemplateItem = {
  id: number;
  coverUrl: string;
  name: string;
  uuid?: string;
};

interface VideoTemplateChooseModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  templates: TemplateItem[];
  selectedId?: number;
  onCreateTemplateSuccess?: () => void;
  onOpenChange?: (open: boolean) => void;
  onSave?: (template: TemplateItem) => void;
}

/** 视频模板选择弹窗 */
export const VideoTemplateChooseModal: React.FC<VideoTemplateChooseModalProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  templates,
  selectedId: externalSelectedId,
  onCreateTemplateSuccess,
  onSave,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const params = useParams();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const [selectedId, setSelectedId] = useState<number | null>(externalSelectedId ?? null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);

  const [triggerId, setTriggerId] = useState("");

  const { account } = iamAccountHooks.useData();
  // 用户资产数字人和音频列表
  const { agentUserActors, agentUserVoices, loading: agentUserLoading } = agentUserVoiceListHooks.useUserActorAndVoiceData();
  // 模板相关
  const { loading: saveLoading, save } = agentTemplateHooks.useSaveAgentTemplate();
  const { loading: detailLoading, fetchDetail } = agentTemplateHooks.useTemplateDetail();
  const { loading: updateLoading, update } = agentTemplateHooks.useUpdateAgentTemplate();
  // const { loading: deleteLoading, deleteTemplate } = agentTemplateHooks.useDeleteAgentTemplate();
  const { loading: deleteLoading, deleteTemplate } = agentUserTemplateListHooks.useDeleteTemplate();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;
  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 定义状态
  const [isPendingAdd, setIsPendingAdd] = useState(false);

  // 微应用 Hook
  const {
    env,
    initialAppLoading,
    enablePresetTemplates,
    handleMicroAppMessage,
    handleMounted,
    handleUnmounted,
    openEditor
  } = useStyleEditorMicroApp({
    account,
    openPresetTemplates: true,
    openPublicActors: true,
    openPublicVoices: true,
    actors: agentUserActors?.filter(item => item.generateStatus === 2).map(item => ({
      id: item.id,
      name: item.name,
      coverUrl: item.image,
      renderUrl: item.image,
      width: item.width,
      height: item.height,
      modelId: item.modelId
    })),
    voices: agentUserVoices.map((item, index) => ({
      id: item.voiceTtsId,
      name: item.name,
      exampleUrl: item.audioUrl,
    })),
    onSaveSuccess: async ({ styleSaveParams, actorModelId }) => {
      if (!triggerId) {
        // 构建新建保存参数
        const templateName = enablePresetTemplates.find(item => item.templateId === styleSaveParams.selectedId)?.templateName || '';

        const createParams = {
          styleId: styleSaveParams.styleId,
          templateName,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          coverUrl: styleSaveParams.coverUrl,
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
          templateId: styleSaveParams.templateId,
        }
        console.log('[miuta]构建创建请求参数', { createParams, styleSaveParams, enablePresetTemplates })
        await save(createParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        onCreateTemplateSuccess?.();
      } else {
        // 构建更新保存参数
        const updateParams = {
          uuid: triggerId,
          styleId: styleSaveParams.styleId,
          coverUrl: styleSaveParams.coverUrl,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
        }
        console.log('[miuta]构建更新请求参数', { updateParams, styleSaveParams, enablePresetTemplates })
        await update(updateParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        setTriggerId("");
        onCreateTemplateSuccess?.();
      }
    },
    onClose: async () => {
      setTriggerId("");
    }
  });

  // 统一的数据加载状态
  const isLoading = agentUserLoading || initialAppLoading;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  useEffect(() => {
    setSelectedId(externalSelectedId ?? null);
  }, [externalSelectedId]);


  // 处理样式选择
  const handleSelected = (id: number) => {
    setSelectedId(id);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    onSave?.(templates.find((item) => item.id === selectedId));
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  // 处理滚动逻辑
  useEffect(() => {
    if (currentOpen && selectedId) {
      // 使用 setTimeout 确保 DOM 已经完全渲染并计算了位置
      const timer = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',  // 滚动到容器中心位置
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentOpen, selectedId]);

  useEffect(() => {
    // 当加载完成，且用户处于“等待新增”的状态时
    if (!isLoading && isPendingAdd) {
      setIsPendingAdd(false); // 重置状态

      if (enablePresetTemplates.length > 0) {
        openEditor({});
      } else {
        toast.toast({
          description: t("agent_creation.subscription_settings.no_preset_template"),
        });
      }
    }
  }, [isLoading, isPendingAdd, enablePresetTemplates, openEditor]);

  // 新建-打开模板弹窗
  const handleAdd = () => {
    if (isLoading) {
      setIsPendingAdd(true);
      return;
    }

    if (enablePresetTemplates?.length > 0) {
      openEditor({});
    } else {
      toast.toast({
        description: t("agent_creation.subscription_settings.no_preset_template"),
      });
    }
  };

  // 打开编辑模板
  const handleEdit = async (uuid?: string) => {
    setTriggerId(uuid || "");
    if (uuid) {
      // 获取模板详情
      const detail = await fetchDetail(uuid);
      // 取出数字人信息
      const actorInfo = agentUserActors?.find(item => item.modelId === detail.actorModelId)
      // 处理编辑参数
      const editIdParam = {
        coverUrl: detail.coverUrl,
        styleId: detail.styleModelId,
        digitalHumanInfo: {
          id: actorInfo?.id,
          name: detail.actorName,
          width: actorInfo?.width,
          height: actorInfo?.height,
          coverUrl: detail.actorPicUrl,
          renderUrl: detail.actorPicUrl,
        },
        voiceInfo: {
          id: detail.voiceModelId,
          name: detail.voiceName,
          coverUrl: detail.coverUrl,
          exampleUrl: detail.voiceSampleUrl,
          speedRatio: detail.speedRatio,
        }
      }
      openEditor({ editIdParam });
    }
  }

  // 删除模板
  const handleDelete = async (uuid: string) => {
    await deleteTemplate(uuid)
    onCreateTemplateSuccess?.()
  }

  // 重命名模板
  const handleRename = async (uuid: string, name: string) => {
    await update({ uuid, templateName: name }, {
      userId: account?.id || '',
      type: 1,
      agentId: params.id,
      autoRefresh: false
    });
    onCreateTemplateSuccess?.()
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[750px] h-[755px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t('agent_creation.template_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='relative flex-1 flex flex-col px-6 pt-5 select-none mb-5 overflow-hidden'>
          <div className='w-full h-full relative overflow-hidden'>
            <div
              className='pt-0.5 pl-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
              <div className='grid grid-cols-6 gap-x-3 gap-y-5'>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <div
                      className={cn(
                        "relative mb-2.5 w-full aspect-[3/4] rounded-[8px] bg-[rgba(234,236,240,1)] cursor-pointer flex items-center justify-center ",
                        // agentUserLoading && "cursor-not-allowed pointer-events-none"
                      )}
                      onClick={handleAdd}
                    >
                      <AddTemplateIcon className="text-[rgba(152,162,179,1)]" />
                      <LoadingOverlay loading={isPendingAdd} />
                    </div>
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_creation.subscription_settings.add_template")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                {templates?.map((item, index) => {
                  const isSelected = selectedId === item.id;
                  return (
                    <div
                      key={`${item.id}-${index}`}
                      className="w-full h-full relative group"
                      {...(isSelected ? { ref: selectedItemRef } : {})}
                    >
                      <div
                        className={cn(
                          "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                          selectedId === item.id
                            ? "outline outline-2 outline-i-normal"
                            : "outline outline-transparent"
                        )}
                        onClick={() => handleSelected(item.id)}
                      >
                        <div className='size-full rounded-[12px] flex-shrink-0'>
                          <FallbackImage
                            primarySrc={buildImageOssResize(item.coverUrl, { w: 102 * 3, h: 136 * 3 })}
                            fallbackSrc={item.coverUrl}
                            className="size-full rounded-[12px] object-cover border border-[#000000]/10"
                          />
                          <IconButton
                            icon={<Eye className="size-5 text-white " />}
                            className='absolute left-2 bottom-1.5 z-50 transition-all opacity-0 group-hover:opacity-100 bg-black/40 hover:bg-black/60 '
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPreview(true);
                              setCurrentIndex(index);
                            }}
                          />
                        </div>
                        {/* 选中样式 */}
                        <div
                          className={cn(
                            "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                            selectedId === item.id && "opacity-100"
                          )}
                        >
                        </div>
                        <Check className={cn(
                          "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                          selectedId === item.id && "opacity-100"
                        )} />
                      </div>
                      <div className="w-full text-center mt-3">
                        <div className="w-full">
                          <TruncatedText text={item.name} className='max-w-[90%] text-sm text-[#333333]' />
                        </div>
                      </div>
                      {!agentUserLoading && <div
                        className="opacity-0 group-hover:opacity-100 absolute top-1.5 right-3 transition-all z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreActionPopover
                          className="w-32"
                          side="bottom"
                          closeOnScroll
                          tiggerIcon={
                            <MoreIcon className="size-5 !text-white" />
                          }
                          tiggerClassName="bg-[rgba(0,0,0,0.4)] hover:bg-[rgba(0,0,0,0.6)]"
                        >
                          <MoreActionItem
                            icon={<EditIcon className="w-4 h-4" />}
                            label={t("common.edit")}
                            className='text-[rgba(16,24,40,1)] font-medium'
                            onClick={() => handleEdit(item.uuid)}
                          />
                          {/* <RenameModal
                            currentName={item.name}
                            trigger={
                              <MoreActionItem
                                icon={<RenameIcon className="w-4 h-4" />}
                                label={t("common.rename")}
                              />
                            }
                            onConfirm={(name) => handleRename(item.uuid, name)}
                          /> */}
                          <DeleteConfirmModal
                            trigger={
                              <MoreActionItem
                                icon={<TrashIcon className="w-4 h-4" />}
                                className='text-[rgba(16,24,40,1)] font-medium'
                                label={t("common.delete")}
                              />
                            }
                            loading={deleteLoading}
                            onConfirm={() => handleDelete(item.uuid)}
                          />
                        </MoreActionPopover>
                      </div>}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <LoadingOverlay loading={saveLoading || updateLoading || detailLoading} />
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 z-20'>
          <TextButton
            label={t('agent_creation.template_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_creation.template_modal.confirm')}
            className='w-[135px] h-11'
            onClick={handleSave}
          />
        </div>
        {/* 预览弹窗 */}
        {showPreview && (
          <PreviewVideoImageFull
            open={showPreview}
            previewList={templates.map((item) => ({
              url: item.coverUrl,
              type: "image",
            }))}
            currentIndex={currentIndex}
            onOpenChange={setShowPreview}
          />
        )}
        {createPortal(
          <div
            id="micro-app-portal"
            className='z-[999999] scrollbar-thin'
            style={{ pointerEvents: 'auto' }}
            // 阻止滚动事件冒泡到 document/body
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <StyleEditorMicroApp
              name="style-editor-microapp"
              env={env}
              onMicroAppMessage={handleMicroAppMessage}
              onMounted={handleMounted}
              onUnmounted={handleUnmounted}
            />
          </div>,
          document.body
        )}
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
